/* ============================================
   PETIKIDS CHARM SELECTOR — Main Logic
   ============================================ */
(function () {
  'use strict';

  /* ---------- STATE ---------- */
  const state = {
    data: null,
    activeCategory: 'all',
    activeSubcategory: null,
    searchQuery: '',
    selected: {},        // { charmId: quantity }
    isShopify: false,
    charmVariantId: null,
    imageBaseUrl: '',
    atcGate: { enabled: false, scope: 'sticky', message: '' },
  };

  /* ---------- HELPERS ---------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }

  function formatPrice(amount) {
    return amount.toFixed(2).replace('.', ',') + '\u00a0\u20ac';
  }

  function totalCharms() {
    return Object.values(state.selected).reduce((s, q) => s + q, 0);
  }

  function totalPrice() {
    const billable = Math.max(0, totalCharms() - freeQuota());
    return billable * (state.data?.config?.price || 1.5);
  }

  function resolveImage(img) {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return (state.imageBaseUrl ? state.imageBaseUrl : '') + img;
  }

  function allCharms() {
    if (!state.data) return [];
    const seen = new Set();
    const charms = [];
    state.data.categories.forEach(cat => {
      cat.charms.forEach(c => {
        // Dédup multi-catégorie : un charm n'apparaît qu'une fois dans "Tous",
        // sous sa catégorie primaire (premier match dans la liste de catégories).
        if (seen.has(c.id)) return;
        seen.add(c.id);
        charms.push({ ...c, categoryId: cat.id, categoryName: cat.name, categoryEmoji: cat.emoji });
      });
    });
    return charms;
  }

  function isOutOfStock(charm) {
    return typeof charm.stock === 'number' && charm.stock <= 0;
  }

  function maxAddable(charm) {
    if (typeof charm.stock === 'number') return charm.stock;
    return state.data?.config?.max_per_charm || 99;
  }

  function freeQuota() {
    const base = state.data?.config?.free_charm_quota || 0;
    if (base <= 0) return 0;
    return base * Math.max(1, getProductQuantity());
  }

  function cumulativeBeforeThis(charmId) {
    let acc = 0;
    for (const id of Object.keys(state.selected)) {
      if (String(id) === String(charmId)) break;
      acc += state.selected[id];
    }
    return acc;
  }

  function filteredCharms() {
    let charms = [];

    if (state.activeCategory === 'all') {
      charms = allCharms();
    } else {
      const cat = state.data.categories.find(c => c.id === state.activeCategory);
      if (!cat) return [];
      charms = cat.charms.map(c => ({
        ...c, categoryId: cat.id, categoryName: cat.name, categoryEmoji: cat.emoji,
      }));

      if (state.activeSubcategory) {
        charms = charms.filter(c => c.subcategory === state.activeSubcategory);
      }
    }

    if (state.searchQuery.trim()) {
      const q = state.searchQuery.trim().toLowerCase();
      charms = charms.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.sku.toLowerCase().includes(q)
      );
    }

    return charms;
  }

  /* ---------- RENDER ---------- */
  function render() {
    const root = qs('#charm-selector-root');
    if (!root || !state.data) return;

    root.innerHTML = buildHTML();
    bindEvents();
    updateAtcGate();
  }

  function buildHTML() {
    const d = state.data;
    const price = d.config.price;

    return `
      <div class="charm-selector">
        ${buildTabs()}
        ${buildSubcategories()}
        ${buildSearch()}
        ${buildGrid()}
        <div class="charm-toast" id="charm-toast"></div>
      </div>
    `;
  }

  function buildTabs() {
    const cats = state.data.categories;
    const allCount = allCharms().length;

    let html = '<div class="charm-tabs">';

    cats.forEach(cat => {
      if (cat.charms.length === 0) return;
      const active = state.activeCategory === cat.id ? 'charm-tab--active' : '';
      html += `<button class="charm-tab ${active}" data-cat="${cat.id}">
        <span class="charm-tab__emoji">${cat.emoji}</span>
        <span class="charm-tab__name">${cat.name}</span>
        <span class="charm-tab__count">${cat.charms.length}</span>
      </button>`;
    });

    html += `<button class="charm-tab ${state.activeCategory === 'all' ? 'charm-tab--active' : ''}" data-cat="all">
      <span class="charm-tab__emoji">\u2728</span>
      <span class="charm-tab__name">Tous</span>
      <span class="charm-tab__count">${allCount}</span>
    </button>`;

    html += '</div>';
    return html;
  }

  function buildSubcategories() {
    if (state.activeCategory === 'all') return '';
    const cat = state.data.categories.find(c => c.id === state.activeCategory);
    if (!cat || !cat.subcategories || cat.subcategories.length === 0) return '';

    let html = '<div class="charm-subcategories">';
    html += `<button class="charm-subcategory ${!state.activeSubcategory ? 'charm-subcategory--active' : ''}" data-sub="">Tous</button>`;
    cat.subcategories.forEach(sub => {
      const active = state.activeSubcategory === sub.id ? 'charm-subcategory--active' : '';
      html += `<button class="charm-subcategory ${active}" data-sub="${sub.id}">${sub.name}</button>`;
    });
    html += '</div>';
    return html;
  }

  function buildSearch() {
    return `
      <div class="charm-search">
        <svg class="charm-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input type="text" class="charm-search__input" placeholder="Rechercher un charm..." value="${state.searchQuery}">
      </div>
    `;
  }

  function buildGrid() {
    const charms = filteredCharms();

    if (charms.length === 0) {
      return `<div class="charm-grid">
        <div class="charm-grid__empty">
          <div class="charm-grid__empty-icon">\ud83d\udd0d</div>
          <div>Aucun charm trouv\u00e9 dans cette cat\u00e9gorie</div>
        </div>
      </div>`;
    }

    let html = '<div class="charm-grid">';

    if (state.activeCategory === 'all' && !state.searchQuery.trim()) {
      let currentCat = null;
      charms.forEach(c => {
        if (c.categoryId !== currentCat) {
          currentCat = c.categoryId;
          html += `<div class="charm-grid__section-title"><span>${c.categoryEmoji}</span> ${c.categoryName}</div>`;
        }
        html += buildCard(c);
      });
    } else {
      charms.forEach(c => { html += buildCard(c); });
    }

    html += '</div>';
    return html;
  }

  function buildCard(charm) {
    const qty = state.selected[charm.id] || 0;
    const oos = isOutOfStock(charm);
    const selectedClass = qty > 0 ? 'charm-card--selected' : '';
    const oosClass = oos ? 'charm-card--out-of-stock' : '';
    const img = resolveImage(charm.image);
    const stockBadge = oos ? '<div class="charm-card__stock-badge">Rupture</div>' : '';
    const ariaDisabled = oos ? 'aria-disabled="true"' : '';
    let freeBadge = '';
    let extraBadge = '';
    if (qty > 0) {
      let freeApplied = 0;
      if (freeQuota() > 0) {
        const cumulative = cumulativeBeforeThis(charm.id);
        const freeRemaining = Math.max(0, freeQuota() - cumulative);
        freeApplied = Math.min(qty, freeRemaining);
      }
      const extra = qty - freeApplied;
      if (freeApplied > 0) freeBadge = `<div class="charm-card__free-badge">INCLUS</div>`;
      if (extra > 0) {
        const unitPrice = state.data?.config?.price || 0;
        const priceStr = Number.isInteger(unitPrice) ? unitPrice : unitPrice.toFixed(2).replace(/\.?0+$/, '');
        extraBadge = `<div class="charm-card__extra-badge">SUPPLÉMENT +${priceStr}€</div>`;
      }
    }

    return `
      <div class="charm-card ${selectedClass} ${oosClass}" data-charm-id="${charm.id}" title="${charm.name} (${charm.sku})" ${ariaDisabled}>
        <div class="charm-card__badge">${qty}</div>
        ${stockBadge}
        ${freeBadge}
        ${extraBadge}
        <div class="charm-card__image-wrap">
          ${img ? `<img class="charm-card__image" src="${img}" alt="${charm.name}" loading="lazy">` : `<span style="font-size:2rem;">\u2728</span>`}
        </div>
        <div class="charm-card__name">${charm.name}</div>
        <div class="charm-card__qty">
          <button class="charm-card__qty-btn" data-action="minus" data-charm="${charm.id}" ${oos ? "disabled" : ""}>\u2212</button>
          <span class="charm-card__qty-value">${qty}</span>
          <button class="charm-card__qty-btn" data-action="plus" data-charm="${charm.id}" ${oos ? "disabled" : ""}>+</button>
        </div>
      </div>
    `;
  }


  function getSelectedCharms() {
    const all = allCharms();
    const result = [];
    Object.keys(state.selected).forEach(id => {
      const qty = state.selected[id];
      if (qty <= 0) return;
      const charm = all.find(c => String(c.id) === String(id));
      if (charm) result.push({ charm, qty });
    });
    return result;
  }

  /* ---------- CART SYNC ---------- */
  function getCurrentMainVariantId() {
    const form = document.querySelector('form[action^="/cart/add"]');
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input && input.value) return String(input.value);
    }
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariantId) {
      return String(window.ShopifyAnalytics.meta.selectedVariantId);
    }
    return null;
  }

  function getCurrentProductId() {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.id) {
      return String(window.ShopifyAnalytics.meta.product.id);
    }
    if (window.meta && window.meta.product && window.meta.product.id) {
      return String(window.meta.product.id);
    }
    return null;
  }

  async function fetchCartFull() {
    const empty = { charms: {}, charmLines: {}, mainInCart: false };
    if (!state.charmVariantId) return empty;
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return empty;
      const cart = await r.json();
      const all = allCharms();
      const charms = {};
      const charmLines = {};
      const mainVariantId = getCurrentMainVariantId();
      const currentProductId = getCurrentProductId();
      let mainInCart = false;
      // First pass: detect if main is in cart
      (cart.items || []).forEach(item => {
        if (mainVariantId && String(item.variant_id) === mainVariantId) mainInCart = true;
      });
      // Second pass: collect charms scoped to the current product
      (cart.items || []).forEach(item => {
        if (String(item.variant_id) !== String(state.charmVariantId)) return;
        const sku = item.properties && (item.properties._SKU || item.properties.SKU);
        if (!sku) return;
        const charm = all.find(c => c.sku === sku);
        if (!charm) return;
        // Scope filter: only include charms tagged for the current product.
        // Legacy untagged items: include only if main is in cart (best-effort association).
        const itemProductTag = item.properties && item.properties._for_product;
        if (currentProductId) {
          if (itemProductTag) {
            if (String(itemProductTag) !== currentProductId) return;
          } else {
            if (!mainInCart) return;
          }
        }
        charms[charm.id] = (charms[charm.id] || 0) + item.quantity;
        if (!charmLines[charm.sku]) charmLines[charm.sku] = { key: item.key, qty: item.quantity };
      });
      return { charms, charmLines, mainInCart };
    } catch (e) {
      console.warn('[CharmSelector] cart fetch failed', e);
      return empty;
    }
  }

  async function fetchCartCharms() {
    return (await fetchCartFull()).charms;
  }

  async function syncFromCart() {
    if (!state.data || !state.charmVariantId || state.syncPaused) return;
    const cartCharms = await fetchCartCharms();
    state.selected = { ...cartCharms };
    refreshCards();
    if (typeof updatePriceSummary === 'function') updatePriceSummary();
    updateAtcGate();
    console.log('[CharmSelector] synced from cart:', JSON.stringify(state.selected));
  }

  /* ---------- ATC GATE ---------- */
  function ensureGateBanner(wrap, beforeEl) {
    if (!wrap) return null;
    let banner = wrap.querySelector(':scope > .charm-atc-gate-banner');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'charm-atc-gate-banner';
      wrap.insertBefore(banner, beforeEl || wrap.firstChild);
    }
    return banner;
  }

  function updateAtcGate() {
    if (!state.atcGate.enabled) return;
    const gated = totalCharms() === 0;
    const msg = state.atcGate.message || 'Sélectionnez au moins 1 charm pour ajouter au panier';

    document.documentElement.classList.toggle('charms-required-not-met', gated);

    // Clean up legacy banners from previous versions
    document.querySelectorAll('.sticky-atc-container > .charm-atc-gate-banner, .sticky-atc__button > .charm-atc-gate-banner').forEach(b => b.remove());

    // Sticky ATC: banner spans full width above the sticky bar container
    document.querySelectorAll('sticky-atc [id^="SectionAtcBtn-"]').forEach(btn => {
      // Restore label if a previous version replaced it
      const labelEl = btn.querySelector('.sticky-atc__label__text');
      if (labelEl && labelEl.dataset.charmOriginalLabel != null) {
        labelEl.textContent = labelEl.dataset.charmOriginalLabel;
        delete labelEl.dataset.charmOriginalLabel;
      }
      if (gated) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');

      const sticky = btn.closest('sticky-atc');
      const container = sticky && sticky.querySelector('.sticky-atc-container');
      const banner = ensureGateBanner(sticky, container);
      if (banner) banner.textContent = msg;
    });

    // Optional: main product ATC button
    if (state.atcGate.scope === 'both') {
      document.querySelectorAll('product-form .product-form__submit').forEach(btn => {
        if (gated) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
        const productForm = btn.closest('product-form') || btn.parentElement;
        const banner = ensureGateBanner(productForm, btn);
        if (banner) banner.textContent = msg;
      });
    }
  }

  /* ---------- EVENTS ---------- */
  function bindEvents() {
    // Tab clicks
    qsa('.charm-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const tabsEl = qs('.charm-tabs');
        const prevScroll = tabsEl ? tabsEl.scrollLeft : 0;
        state.activeCategory = tab.dataset.cat;
        state.activeSubcategory = null;
        render();
        const newTabsEl = qs('.charm-tabs');
        if (newTabsEl) newTabsEl.scrollLeft = prevScroll;
      });
    });

    // Subcategory clicks
    qsa('.charm-subcategory').forEach(btn => {
      btn.addEventListener('click', () => {
        state.activeSubcategory = btn.dataset.sub || null;
        render();
      });
    });

    // Search input
    const searchInput = qs('.charm-search__input');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        state.searchQuery = e.target.value;
        renderGrid();
      });
    }

    // Card clicks (delegation)
    const grid = qs('.charm-grid');
    if (grid) {
      grid.addEventListener('click', handleGridClick);
    }

    // Intercept theme's main ATC button
    interceptThemeATC();
  }

  /* ---------- INTERCEPT THEME ATC (fetch patch for FormData) ---------- */
  function interceptThemeATC() {
    if (window.__charmFetchPatched) return;
    window.__charmFetchPatched = true;

    const _origFetch = window.fetch.bind(window);

    function isCartMutation(urlStr, method) {
      if (method !== 'POST') return false;
      return urlStr.indexOf('/cart/add') !== -1
          || urlStr.indexOf('/cart/change') !== -1
          || urlStr.indexOf('/cart/update') !== -1
          || urlStr.indexOf('/cart/clear') !== -1;
    }

    window.fetch = function(url, options) {
      const urlStr = (typeof url === 'string') ? url : (url && url.url ? url.url : '');
      const method = (options && options.method) ? options.method.toUpperCase() : 'GET';
      const isCartAdd = urlStr.indexOf('/cart/add') !== -1 && method === 'POST';

      if (!isCartAdd) {
        return _origFetch(url, options);
      }

      const selectedCharms = getSelectedCharms();
      console.log('[CharmSelector] fetch /cart/add intercepted | state.selected:', JSON.stringify(state.selected), '| matchedCharms:', selectedCharms.length, '| charmVariantId:', state.charmVariantId);

      if (state.atcGate.enabled && selectedCharms.length === 0) {
        console.log('[CharmSelector] Gate active: blocking cart add (no charm selected)');
        showToast(state.atcGate.message || 'Sélectionnez au moins 1 charm');
        return Promise.reject(new Error('charm-gate'));
      }

      if (selectedCharms.length === 0 || !state.charmVariantId) {
        console.log('[CharmSelector] No charms selected, passing through');
        return _origFetch(url, options);
      }

      const currentProductId = getCurrentProductId();

      function resetSelectionAfterAdd() {
        state.selected = {};
        if (typeof refreshCards === 'function') refreshCards();
        if (typeof updatePriceSummary === 'function') updatePriceSummary();
        if (typeof updateAtcGate === 'function') updateAtcGate();
      }

      // Pure additive: append selected charms to the existing /cart/add request.
      if (options.body instanceof FormData) {
        const fd = options.body;
        const hasItems = Array.from(fd.keys()).some(function(k) { return k.indexOf('items[') === 0; });
        let nextIdx = 0;
        if (hasItems) {
          Array.from(fd.keys()).forEach(function(k) {
            const m = k.match(/^items\[(\d+)\]/);
            if (m) nextIdx = Math.max(nextIdx, parseInt(m[1]) + 1);
          });
        } else {
          var origId = fd.get('id');
          var origQty = fd.get('quantity') || '1';
          fd.delete('id');
          fd.delete('quantity');
          fd.append('items[0][id]', origId);
          fd.append('items[0][quantity]', origQty);
          nextIdx = 1;
        }
        selectedCharms.forEach(function(entry) {
          fd.append('items[' + nextIdx + '][id]', String(state.charmVariantId));
          fd.append('items[' + nextIdx + '][quantity]', String(entry.qty));
          fd.append('items[' + nextIdx + '][properties][Charm]', entry.charm.name);
          fd.append('items[' + nextIdx + '][properties][_SKU]', entry.charm.sku || '');
          if (currentProductId) fd.append('items[' + nextIdx + '][properties][_for_product]', currentProductId);
          var charmImg = resolveImage(entry.charm.image);
          if (charmImg) fd.append('items[' + nextIdx + '][properties][_charm_image]', charmImg);
          nextIdx++;
        });
        return _origFetch(url, options).then(function(r) {
          if (r.ok) resetSelectionAfterAdd();
          return r;
        });
      }

      // JSON body fallback
      if (typeof options.body === 'string') {
        try {
          var body = JSON.parse(options.body);
          var items = [];
          if (body.items && Array.isArray(body.items)) items = body.items.slice();
          else if (body.id) items.push({ id: Number(body.id), quantity: Number(body.quantity) || 1 });
          selectedCharms.forEach(function(entry) {
            var props = { 'Charm': entry.charm.name, '_SKU': entry.charm.sku || '' };
            if (currentProductId) props['_for_product'] = currentProductId;
            var charmImg = resolveImage(entry.charm.image);
            if (charmImg) props['_charm_image'] = charmImg;
            items.push({ id: Number(state.charmVariantId), quantity: entry.qty, properties: props });
          });
          const newOptions = Object.assign({}, options, {
            body: JSON.stringify({ items: items }),
            headers: Object.assign({}, options.headers || {}, { 'Content-Type': 'application/json' })
          });
          return _origFetch(url, newOptions).then(function(r) {
            if (r.ok) resetSelectionAfterAdd();
            return r;
          });
        } catch(e) {
          console.warn('[CharmSelector] Could not parse JSON body:', e);
        }
      }

      return _origFetch(url, options).then(function(r) {
        if (r.ok) resetSelectionAfterAdd();
        return r;
      });
    };

    // Debug helper — type debugCharms() in console
    window.debugCharms = function() {
      console.log('state.selected:', JSON.stringify(state.selected));
      console.log('state.charmVariantId:', state.charmVariantId);
      console.log('allCharms IDs:', allCharms().map(function(c) { return { id: c.id, type: typeof c.id, name: c.name }; }));
      console.log('getSelectedCharms():', JSON.stringify(getSelectedCharms()));
    };

    console.log('[CharmSelector] Fetch interceptor installed (FormData + JSON)');
  }

  function findCharmById(id) {
    return allCharms().find(c => String(c.id) === String(id));
  }

  function canIncrement(charmId) {
    const charm = findCharmById(charmId);
    if (!charm) return false;
    if (isOutOfStock(charm)) return false;
    const cap = maxAddable(charm);
    return (state.selected[charmId] || 0) < cap;
  }

  function handleGridClick(e) {
    // Check if click is on qty buttons
    const qtyBtn = e.target.closest('.charm-card__qty-btn');
    if (qtyBtn) {
      e.stopPropagation();
      if (qtyBtn.disabled) return;
      const charmId = qtyBtn.dataset.charm;
      const action = qtyBtn.dataset.action;

      if (action === 'plus') {
        if (!canIncrement(charmId)) {
          showToast('Stock max atteint pour ce charm');
          return;
        }
        state.selected[charmId] = (state.selected[charmId] || 0) + 1;
      } else if (action === 'minus') {
        state.selected[charmId] = Math.max(0, (state.selected[charmId] || 0) - 1);
        if (state.selected[charmId] <= 0) delete state.selected[charmId];
      }

      afterQtyChange(charmId);
      return;
    }

    // Card click = toggle / add
    const card = e.target.closest('.charm-card');
    if (!card) return;
    if (card.getAttribute('aria-disabled') === 'true') return;

    const charmId = card.dataset.charmId;
    if (!charmId) return;

    if (!canIncrement(charmId)) {
      if (!state.selected[charmId]) showToast('Stock épuisé');
      else showToast('Stock max atteint pour ce charm');
      return;
    }

    if (!state.selected[charmId]) {
      state.selected[charmId] = 1;
      card.classList.add('charm-card--pop');
      setTimeout(() => card.classList.remove('charm-card--pop'), 300);
    } else {
      state.selected[charmId]++;
    }

    afterQtyChange(charmId);
  }

  function afterQtyChange(charmId) {
    if (freeQuota() > 0) {
      refreshCards();
      updatePriceSummary();
      showQuotaStatus();
    } else {
      updateCard(charmId);
    }
    updateAtcGate();
  }

  function showQuotaStatus() {
    const quota = freeQuota();
    if (quota <= 0) return;
    const total = totalCharms();
    if (total <= 0) return;
    const included = Math.min(total, quota);
    const remaining = Math.max(0, quota - total);
    const extra = Math.max(0, total - quota);
    let msg;
    if (extra > 0) {
      msg = `${quota}/${quota} inclus · +${extra} en supplément`;
    } else if (remaining === 0) {
      msg = `${quota}/${quota} inclus ✓ quota atteint`;
    } else {
      msg = `${included}/${quota} inclus · ${remaining} restant${remaining > 1 ? 's' : ''}`;
    }
    showToast(msg);
  }

  function refreshCards() {
    qsa('.charm-card').forEach(card => {
      const cid = card.dataset.charmId;
      const qty = state.selected[cid] || 0;

      if (qty > 0) card.classList.add('charm-card--selected');
      else card.classList.remove('charm-card--selected');

      const badge = card.querySelector('.charm-card__badge');
      if (badge) badge.textContent = qty;
      const qtyVal = card.querySelector('.charm-card__qty-value');
      if (qtyVal) qtyVal.textContent = qty;

      let freeApplied = 0;
      if (freeQuota() > 0 && qty > 0) {
        const cumulative = cumulativeBeforeThis(cid);
        const freeRemaining = Math.max(0, freeQuota() - cumulative);
        freeApplied = Math.min(qty, freeRemaining);
      }
      const extra = Math.max(0, qty - freeApplied);
      let freeEl = card.querySelector('.charm-card__free-badge');
      if (freeApplied > 0) {
        if (freeEl) {
          freeEl.textContent = 'INCLUS';
        } else {
          freeEl = document.createElement('div');
          freeEl.className = 'charm-card__free-badge';
          freeEl.textContent = 'INCLUS';
          card.appendChild(freeEl);
        }
      } else if (freeEl) {
        freeEl.remove();
      }
      let extraEl = card.querySelector('.charm-card__extra-badge');
      if (extra > 0) {
        const unitPrice = state.data?.config?.price || 0;
        const priceStr = Number.isInteger(unitPrice) ? unitPrice : unitPrice.toFixed(2).replace(/\.?0+$/, '');
        const extraText = `SUPPLÉMENT +${priceStr}€`;
        if (extraEl) {
          extraEl.textContent = extraText;
        } else {
          extraEl = document.createElement('div');
          extraEl.className = 'charm-card__extra-badge';
          extraEl.textContent = extraText;
          card.appendChild(extraEl);
        }
      } else if (extraEl) {
        extraEl.remove();
      }
    });
  }

  function updateCard(charmId) {
    const card = qs(`.charm-card[data-charm-id="${charmId}"]`);
    if (!card) return;

    const qty = state.selected[charmId] || 0;

    if (qty > 0) {
      card.classList.add('charm-card--selected');
    } else {
      card.classList.remove('charm-card--selected');
    }

    const badge = card.querySelector('.charm-card__badge');
    if (badge) badge.textContent = qty;

    const qtyValue = card.querySelector('.charm-card__qty-value');
    if (qtyValue) qtyValue.textContent = qty;

    updatePriceSummary();
  }

  function updatePriceSummary() {
    const charmTotal = totalPrice();

    // Find all price elements that should be updated
    // 1. Sticky bar separate price
    const stickyPriceEl = qs('[id^="sticky-atc-separate-price-"] .price-item--regular')
                       || qs('[id^="sticky-atc-separate-price-"] .price-item--sale');
    // 2. Sticky bar button price
    const stickyBtnPrice = qs('[id^="sticky-atc-price-"]');
    // 3. Main ATC button price
    const mainAtcPrice = qs('[id^="main-atc-price-"]');

    // Store original prices on first call
    if (!state._origPrices) {
      state._origPrices = {};
      if (stickyPriceEl) state._origPrices.sticky = parsePriceCents(stickyPriceEl.textContent);
      if (stickyBtnPrice) state._origPrices.stickyBtn = parsePriceCents(stickyBtnPrice.textContent);
      if (mainAtcPrice) state._origPrices.mainAtc = parsePriceCents(mainAtcPrice.textContent);
    }

    const charmCents = Math.round(charmTotal * 100);

    // Update each price element
    if (stickyPriceEl && state._origPrices.sticky != null) {
      stickyPriceEl.textContent = formatPriceCents(state._origPrices.sticky + charmCents);
    }
    if (stickyBtnPrice && state._origPrices.stickyBtn != null) {
      const bullet = stickyBtnPrice.textContent.includes('\u2022') || stickyBtnPrice.textContent.includes('•') ? ' •' : '';
      stickyBtnPrice.textContent = formatPriceCents(state._origPrices.stickyBtn + charmCents) + bullet;
    }
    if (mainAtcPrice && state._origPrices.mainAtc != null) {
      const bullet = mainAtcPrice.textContent.includes('\u2022') || mainAtcPrice.textContent.includes('•') ? ' •' : '';
      mainAtcPrice.textContent = formatPriceCents(state._origPrices.mainAtc + charmCents) + bullet;
    }
  }

  function parsePriceCents(str) {
    if (!str) return null;
    // Extract digits from price string like "€13,00" or "13,00 €" or "€13.00"
    const cleaned = str.replace(/[^0-9.,]/g, '').trim();
    if (!cleaned) return null;
    // Handle comma as decimal separator (European format)
    const parts = cleaned.split(/[.,]/);
    if (parts.length >= 2) {
      const last = parts.pop();
      return parseInt(parts.join('')) * 100 + parseInt(last.padEnd(2, '0').substring(0, 2));
    }
    return parseInt(cleaned) * 100;
  }

  function formatPriceCents(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return '\u20ac' + euros;
  }

  function renderGrid() {
    const gridContainer = qs('.charm-grid');
    if (!gridContainer) return;

    const charms = filteredCharms();

    if (charms.length === 0) {
      gridContainer.innerHTML = `
        <div class="charm-grid__empty">
          <div class="charm-grid__empty-icon">\ud83d\udd0d</div>
          <div>Aucun charm trouv\u00e9</div>
        </div>`;
      return;
    }

    let html = '';
    if (state.activeCategory === 'all' && !state.searchQuery.trim()) {
      let currentCat = null;
      charms.forEach(c => {
        if (c.categoryId !== currentCat) {
          currentCat = c.categoryId;
          html += `<div class="charm-grid__section-title"><span>${c.categoryEmoji}</span> ${c.categoryName}</div>`;
        }
        html += buildCard(c);
      });
    } else {
      charms.forEach(c => { html += buildCard(c); });
    }

    gridContainer.innerHTML = html;
  }

  /* ---------- PRODUCT VARIANT DETECTION ---------- */
  function getProductVariantId() {
    // 1. URL param
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('variant');
    if (fromUrl) return fromUrl;

    // 2. Product form hidden input
    const form = document.querySelector('form[action="/cart/add"]') ||
                 document.querySelector('product-form form') ||
                 document.querySelector('[data-product-form]');
    if (form) {
      const input = form.querySelector('input[name="id"]') ||
                    form.querySelector('select[name="id"]');
      if (input) return input.value;
    }

    // 3. Shopify global product JSON
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariantId) {
      return String(window.ShopifyAnalytics.meta.selectedVariantId);
    }

    return null;
  }

  function getMainQtyInput() {
    return document.querySelector('quantity-input.main-quantity input[name="quantity"]')
        || document.querySelector('input.quantity__input[name="quantity"]')
        || document.querySelector('form[action^="/cart/add"] input[name="quantity"]')
        || document.querySelector('input[name="quantity"]');
  }

  function getProductQuantity() {
    const qtyInput = getMainQtyInput();
    return qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
  }

  /* ---------- SHOPIFY CART API ---------- */
  async function addToCart() {
    const selectedCharms = getSelectedCharms();
    const productVariantId = getProductVariantId();
    const charmVariantId = state.charmVariantId;

    // Nothing to add?
    if (selectedCharms.length === 0 && !productVariantId) {
      showToast('S\u00e9lectionne au moins un charm !');
      return;
    }

    const addBtn = qs('#charm-add-btn');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Ajout en cours...';
    }

    // Build items array: PRODUCT + CHARMS
    const items = [];

    // Add the main product (trousse/pochette)
    if (productVariantId) {
      items.push({
        id: Number(productVariantId),
        quantity: getProductQuantity(),
      });
    }

    // Add each selected charm
    if (charmVariantId && selectedCharms.length > 0) {
      const currentProductId = getCurrentProductId();
      selectedCharms.forEach(({ charm, qty }) => {
        const props = {
          'Charm': charm.name,
          '_SKU': charm.sku || '',
        };
        if (currentProductId) props['_for_product'] = currentProductId;
        items.push({
          id: Number(charmVariantId),
          quantity: qty,
          properties: props
        });
      });
    }

    if (items.length === 0) {
      showToast('Rien \u00e0 ajouter');
      if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Ajouter au panier'; }
      return;
    }

    console.log('[CharmSelector] Adding to cart:', JSON.stringify(items, null, 2));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[CharmSelector] Cart API error:', data);
        throw new Error(data.description || data.message || 'Erreur lors de l\u2019ajout');
      }

      console.log('[CharmSelector] Added successfully:', data);

      // Reset charm selection
      state.selected = {};
      render();

      // Redirect to cart page for confirmation
      window.location.href = '/cart';

    } catch (err) {
      console.error('[CharmSelector] Error:', err);
      showToast('\u274c ' + (err.message || 'Erreur lors de l\u2019ajout'));
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.textContent = 'Ajouter au panier';
      }
    }
  }

  /* ---------- TOAST ---------- */
  function showToast(message) {
    const toast = qs('#charm-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('charm-toast--visible');
    setTimeout(() => toast.classList.remove('charm-toast--visible'), 2500);
  }

  /* ---------- INIT ---------- */
  async function init() {
    const root = qs('#charm-selector-root');
    if (!root) return;

    // Check for Shopify context
    state.isShopify = typeof window.Shopify !== 'undefined';

    // Get config from data attributes
    const variantId = root.dataset.charmVariantId;
    if (variantId) state.charmVariantId = variantId;

    const imageBase = root.dataset.imageBaseUrl;
    if (imageBase) state.imageBaseUrl = imageBase;

    state.atcGate.enabled = root.dataset.atcGateEnabled === 'true';
    state.atcGate.scope = root.dataset.atcGateScope || 'sticky';
    state.atcGate.message = root.dataset.atcGateMessage || '';
    if (state.atcGate.enabled) {
      document.documentElement.classList.add('charms-required-not-met');
      updateAtcGate();
    }

    // Load charm data — prefer inline JSON from Liquid, fallback to fetch
    const inlineData = qs('#charm-selector-data');
    let dataUrl = root.dataset.dataUrl || 'assets/charms-data.json';

    try {
      if (inlineData) {
        state.data = JSON.parse(inlineData.textContent);
      } else {
        const resp = await fetch(dataUrl);
        if (!resp.ok) throw new Error('Failed to load charms data');
        state.data = await resp.json();
      }

      if (state.data.config.shopify_charm_variant_id) {
        state.charmVariantId = String(state.data.config.shopify_charm_variant_id);
      }
      if (state.data.config.image_base_url) {
        state.imageBaseUrl = state.data.config.image_base_url;
      }

      // Default to the first real category (instead of "Tous")
      if (state.data.categories && state.data.categories.length > 0) {
        state.activeCategory = state.data.categories[0].id;
      }

      console.log('[CharmSelector] Init OK — charmVariantId:', state.charmVariantId, '| charms:', allCharms().length);
      render();
      // NOTE: ne pas syncFromCart() ici — chaque visite démarre avec une sélection vide
      // pour éviter que les charms d'un ajout précédent restent affichés comme sélectionnés.

      // Re-render quota/badges when the trousse quantity changes — free quota scales with qty
      const qtyInput = getMainQtyInput();
      if (qtyInput) {
        let lastQty = parseInt(qtyInput.value, 10) || 1;
        const onQtyChange = () => {
          if (!state.data) return;
          const newQty = parseInt(qtyInput.value, 10) || 1;
          if (newQty === lastQty) return;
          lastQty = newQty;
          refreshCards();
          updatePriceSummary();
          if (totalCharms() > 0) showQuotaStatus();
          updateAtcGate();
        };
        qtyInput.addEventListener('change', onQtyChange);
        qtyInput.addEventListener('input', onQtyChange);
        // Also catch +/- button clicks on the surrounding quantity-input component
        const qtyHost = qtyInput.closest('quantity-input');
        if (qtyHost) {
          qtyHost.querySelectorAll('button').forEach(btn => {
            btn.addEventListener('click', () => setTimeout(onQtyChange, 0));
          });
        }
      }
    } catch (err) {
      console.error('Charm selector init error:', err);
      root.innerHTML = '<p style="text-align:center;color:#999;padding:20px;">Impossible de charger les charms.</p>';
    }
  }

  // Auto-init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.PetikidsCharmSelector = {
    getSelected: getSelectedCharms,
    getState: () => state,
    refresh: render,
  };
})();
